import { Injectable, ErrorHandler, Injector, inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';

import { JepCurrentAccountService } from '@jepto/shared/services/jep-current-account/jep-current-account.service';

import { JepSnackbarComponent } from '@jepto/shared/components/jep-snackbar/jep-snackbar.component';
import {
	sentryLogEvent,
	sentryLogError,
	sentryLogSuccess,
	isErrorExistingFromGQLResponse,
} from '@jepto/shared/utils/collection';
import { environment } from '@jepto/environments/environment';
import { isChunkLoadFailure, reloadAppWindow } from './chunk-load-error.util';

interface SanitizedError {
	message: string;
	details: string;
}
@Injectable({ providedIn: 'root' })
export class JepErrorHandlerService implements ErrorHandler {
	private isHandlingError = false; // Prevent recursive error handling
	private errorCount = 0; // Track consecutive error handling calls
	private readonly MAX_CONSECUTIVE_ERRORS = 3; // Prevent infinite loops
	private reloadPromptShown = false;
	private readonly injector = inject(Injector);

	constructor(private jepCurrentAccount: JepCurrentAccountService) {}

	handleError(error) {
		// Prevent recursive error handling with multiple safeguards
		if (this.isHandlingError) {
			console.error(
				'Error handler recursion detected, preventing infinite loop:',
				error
			);
			return;
		}

		// Reset error count after a short delay to allow for legitimate error bursts
		setTimeout(() => {
			this.errorCount = 0;
		}, 1000);

		// Prevent too many consecutive errors (likely infinite loop)
		this.errorCount++;
		if (this.errorCount > this.MAX_CONSECUTIVE_ERRORS) {
			console.error(
				'Too many consecutive errors, preventing infinite loop:',
				error
			);
			return;
		}

		this.isHandlingError = true;

		try {
			// Get a safe error message first
			const safeErrorMessage = this.getSafeErrorMessage(error);

			// Stale-tab after deploy: hashed lazy chunks 404. Prompt refresh; do not auto-reload.
			if (
				isChunkLoadFailure(error) ||
				isChunkLoadFailure(safeErrorMessage)
			) {
				this.promptReloadForNewVersion();
				return;
			}

			// Only proceed with full error handling if it's not a chunk loading error
			let sanitisedEror: SanitizedError = this.sanitiseError(error);

			// Safely log the error without throwing
			this.safeLogEvent('Handle Error', sanitisedEror);

			this.notify(sanitisedEror);

			// Safely log the error
			if (environment.staging || environment.production) {
				this.safeLogError(error);
			} else {
				console.error('log error', error);
			}
		} catch (handlerError) {
			// If error handling itself fails, log it safely without any complex operations
			console.error(
				'Error in error handler:',
				this.getSafeErrorMessage(handlerError)
			);
		} finally {
			this.isHandlingError = false;
		}
	}

	private notify(error: SanitizedError) {
		if (environment.production) {
			this.safeLogEvent('Error Handler', error);
			const mutation = `mutation($input: ErrorHandlerInput) { errorHandler(input: $input){ accountId } }`;
			// this.requestService.mutate({ mutation: mutation, variables: { input: { error: error, userKey: this.jepCurrentAccount.getCognitoUserId() } } }).subscribe((gqlResponse: any) => {
			// 	const isErrorExisting: boolean = isErrorExistingFromGQLResponse(gqlResponse);
			// 	if (isErrorExisting === true) {
			// 		sentryLogError(JSON.stringify(gqlResponse));
			// 	} else {
			// 		sentryLogSuccess(JSON.stringify(gqlResponse));
			// 	}
			// });
		}
	}

	private sanitiseError(error: Error): SanitizedError {
		try {
			const message = error?.message || 'Unknown error';
			const stack = error?.stack
				? JSON.stringify(error.stack)
				: 'No stack trace available';
			return { message, details: stack };
		} catch (sanitizeError) {
			// If sanitization fails, return a safe fallback
			return {
				message: 'Error sanitization failed',
				details: 'Could not process error details',
			};
		}
	}

	private safeLogEvent(eventName: string, data: any): void {
		try {
			sentryLogEvent(
				eventName,
				data != null && typeof data === 'object'
					? data
					: { details: data }
			);
		} catch (logError) {
			console.error('Failed to log event:', logError);
		}
	}

	private safeLogError(error: any): void {
		try {
			sentryLogError(error);
		} catch (logError) {
			console.error('Failed to log error:', logError);
		}
	}

	private getSafeErrorMessage(error: any): string {
		try {
			if (error && typeof error === 'object') {
				return error.message || error.toString() || 'Unknown error';
			}
			return String(error || 'Unknown error');
		} catch (e) {
			return 'Error message extraction failed';
		}
	}

	private promptReloadForNewVersion(): void {
		if (this.reloadPromptShown) {
			return;
		}

		const snackBar = this.injector.get(MatSnackBar);
		const ref = snackBar.openFromComponent(JepSnackbarComponent, {
			duration: 0,
			data: {
				text: 'A new version of the app is available. Please refresh to continue.',
				withButton: true,
				customTextButton: 'Refresh',
				status: 'info',
			},
		});
		this.reloadPromptShown = true;
		ref.instance.onClickSnackbar.subscribe(() => {
			reloadAppWindow();
		});
		ref.afterDismissed().subscribe(() => {
			this.reloadPromptShown = false;
		});
	}
}
